import {Box, HStack, Show} from 'platform/foundation';
import {cn, cva} from 'platform/shared';
import {match} from 'ts-pattern';

import {HTMLAttributes, KeyboardEvent, ReactNode, useId, useRef, useState} from 'react';

import {isNilOrEmpty} from 'ramda-adjunct';

import {Nullish, suffixTestId, TestIdProps, useDimensions, useQueryState} from 'shared/web';

import {Actions} from '../Actions/Actions';
import {Separator} from '../Separator/Separator';
import {Tab, TabProps} from './Tab';
import {determineLazyBehavior} from './utils/determineLazyBehavior';

const SEARCH_QUERY_KEY = 'controlled-tab';

export type TabsProps = TestIdProps & {
  variant?: 'container' | 'condensed' | 'fullHeight' | 'default';
  tabs: TabProps[];
  isFullWidth?: boolean;
  children?: ReactNode;
  shouldWrap?: boolean;
  isLazy?: boolean;
  isFullHeight?: boolean;
  hasSeparator?: boolean;
  lazyBehavior?: 'unmount' | 'keepMounted';
} & (
    | {
        activeTabId: string | Nullish;
        onChange: (id: string) => void;
        defaultTabId?: never;
      }
    | {
        activeTabId?: never;
        onChange?: never;
        defaultTabId?: string;
      }
  );

export type StyledTabListProps = {
  $isFullWidth?: TabsProps['isFullWidth'];
  $shouldWrap?: TabsProps['shouldWrap'];
  $variant?: TabsProps['variant'];
};

type StyledTabListComponentProps = StyledTabListProps & HTMLAttributes<HTMLDivElement>;

const tabListVariants = cva('flex flex-row justify-start', {
  variants: {
    shouldWrap: {
      true: 'flex-auto flex-wrap whitespace-nowrap',
      false: 'flex-none flex-nowrap',
    },
    isFullWidth: {
      true: 'flex-[100%]',
      false: '',
    },
    variant: {
      container: '',
      condensed: 'gap-x-6 gap-y-2',
      fullHeight: 'gap-x-6 gap-y-2',
      default: 'gap-x-6 gap-y-2',
    },
  },
  defaultVariants: {
    shouldWrap: false,
    isFullWidth: false,
    variant: 'default',
  },
});

export function StyledTabList(props: StyledTabListComponentProps) {
  const htmlProps = {...props};
  delete htmlProps.$isFullWidth;
  delete htmlProps.$shouldWrap;
  delete htmlProps.$variant;

  return (
    <div
      {...htmlProps}
      className={cn(
        tabListVariants({
          shouldWrap: props.$shouldWrap,
          isFullWidth: props.$isFullWidth,
          variant: props.$variant,
        }),
        props.className
      )}
    />
  );
}

// tw-migration: kept for API parity with the old barrel; the styles live in tabPanelVariants below
export type StyledTabPanelProps = {
  $variant?: TabsProps['variant'];
  $isFullHeight?: TabsProps['isFullHeight'];
};

const tabPanelVariants = cva('w-full px-0 pb-4 pt-4', {
  variants: {
    variant: {
      container: 'px-4',
      condensed: '',
      fullHeight: 'h-full pb-0',
      default: '',
    },
    isFullHeight: {
      true: 'h-full',
      false: '',
    },
  },
  defaultVariants: {
    variant: 'default',
    isFullHeight: false,
  },
});

export function Tabs(props: TabsProps) {
  const queryId = useId();
  const tabIdPrefix = useId();
  const panelIdPrefix = useId();
  const [tabListRef, tabListSize] = useDimensions();
  const hasBeenSelected = useRef<Record<string, boolean>>({});
  const [innerActiveTabId, setInnerActiveTabId] = useQueryState(`${queryId}-${SEARCH_QUERY_KEY}`);

  const activeTabId =
    props.activeTabId ?? innerActiveTabId ?? props.defaultTabId ?? props.tabs[0]?.id;
  const setActiveTabId = props.onChange ?? setInnerActiveTabId;
  const activeTabActions = props.tabs.find((item) => item.id === activeTabId)?.actions;

  // Track focused tab separately from active tab
  const [focusState, setFocusState] = useState<{
    activeTabId: string | Nullish;
    focusedTabId: string | Nullish;
  }>({activeTabId, focusedTabId: null});

  if (focusState.activeTabId !== activeTabId) {
    const focusedTabId =
      focusState.focusedTabId && focusState.focusedTabId !== activeTabId
        ? null
        : focusState.focusedTabId;
    setFocusState({activeTabId, focusedTabId});
  }

  const focusedTabId = focusState.activeTabId === activeTabId ? focusState.focusedTabId : null;
  const setFocusedTabId = (tabId: string | Nullish) => {
    setFocusState({activeTabId, focusedTabId: tabId});
  };

  if (isNilOrEmpty(props.tabs)) {
    return null;
  }

  const handleChangeTab = (tabId: string) => {
    setActiveTabId(tabId);
  };

  const handleFocusTab = (tabId: string) => {
    setFocusedTabId(tabId);
  };

  const getTabId = (tabId: string) => `${tabIdPrefix}-tab-${tabId}`;
  const getPanelId = (tabId: string) => `${panelIdPrefix}-panel-${tabId}`;

  const handleKeyDown = (event: KeyboardEvent<HTMLDivElement>) => {
    const enabledTabs = props.tabs.filter((tab) => !tab.isDisabled);
    const currentFocusedTab = focusedTabId ?? activeTabId;
    const currentIndex = enabledTabs.findIndex((tab) => tab.id === currentFocusedTab);

    if (currentIndex === -1) {
      return;
    }

    const focusTab = (tabId: string) => {
      const tabElement = document.getElementById(getTabId(tabId));
      if (tabElement) {
        tabElement.focus();
      }
    };

    match(event.key)
      .with('ArrowLeft', () => {
        event.preventDefault();
        const prevIndex = currentIndex === 0 ? enabledTabs.length - 1 : currentIndex - 1;
        const prevTab = enabledTabs.at(prevIndex);
        if (prevTab && prevIndex >= 0 && prevIndex < enabledTabs.length) {
          handleFocusTab(prevTab.id);
          focusTab(prevTab.id);
        }
      })
      .with('ArrowRight', () => {
        event.preventDefault();
        const nextIndex = currentIndex === enabledTabs.length - 1 ? 0 : currentIndex + 1;
        const nextTab = enabledTabs.at(nextIndex);
        if (nextTab && nextIndex >= 0 && nextIndex < enabledTabs.length) {
          handleFocusTab(nextTab.id);
          focusTab(nextTab.id);
        }
      })
      .with('Home', () => {
        event.preventDefault();
        const firstTab = enabledTabs.at(0);
        if (firstTab) {
          handleFocusTab(firstTab.id);
          focusTab(firstTab.id);
        }
      })
      .with('End', () => {
        event.preventDefault();
        const lastTab = enabledTabs.at(-1);
        if (lastTab) {
          handleFocusTab(lastTab.id);
          focusTab(lastTab.id);
        }
      })
      .with(' ', 'Enter', () => {
        event.preventDefault();
        if (currentFocusedTab) {
          handleChangeTab(currentFocusedTab);
          // Update focus to match the newly activated tab
          setFocusedTabId(currentFocusedTab);
        }
      })
      .otherwise(() => {});
  };

  return (
    <>
      <div ref={tabListRef}>
        <HStack justify="space-between" spacing={2} wrap>
          <StyledTabList
            role="tablist"
            onKeyDown={handleKeyDown}
            $isFullWidth={props.isFullWidth}
            $shouldWrap={props.shouldWrap}
            $variant={props.variant}
          >
            {props.tabs.map((tab) => (
              <Tab
                key={tab.id}
                id={tab.id}
                title={tab.title}
                icon={tab.icon}
                badge={tab.badge}
                variant={props.variant}
                isDisabled={tab.isDisabled}
                isSelected={tab.id === activeTabId}
                isFocused={tab.id === focusedTabId}
                isFullWidth={props.isFullWidth}
                onClick={handleChangeTab}
                onFocus={handleFocusTab}
                data-testid={tab['data-testid']}
                data-tab-id={tab.id}
                tabId={getTabId(tab.id)}
                panelId={getPanelId(tab.id)}
                tabIndex={tab.id === activeTabId ? 0 : -1}
              />
            ))}
          </StyledTabList>
          <Actions actions={activeTabActions} />
        </HStack>
        <Show when={props.hasSeparator}>
          <Separator spacing={0} />
        </Show>
      </div>
      <Box
        height={props.isFullHeight ? `calc(100% - ${tabListSize.height}px)` : undefined}
        width="100%"
      >
        {props.tabs.map((tab) => {
          const isTabActive = tab.id === activeTabId;

          if (isTabActive) {
            hasBeenSelected.current[tab.id] = true;
          }

          const shouldRenderTab = determineLazyBehavior({
            hasBeenSelected: hasBeenSelected.current[tab.id],
            isSelected: isTabActive,
            isLazy: props.isLazy,
            lazyBehavior: props.lazyBehavior,
          });

          if (!shouldRenderTab) {
            return null;
          }

          return (
            <div
              key={tab.id}
              role="tabpanel"
              id={getPanelId(tab.id)}
              aria-labelledby={getTabId(tab.id)}
              aria-hidden={!isTabActive}
              hidden={!isTabActive}
              tabIndex={-1}
              className={cn(
                tabPanelVariants({variant: props.variant, isFullHeight: props.isFullHeight})
              )}
              data-testid={suffixTestId('tabPanel', {'data-testid': tab['data-testid']})}
            >
              {tab.content}
            </div>
          );
        })}
      </Box>
    </>
  );
}
